import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBusiness, useUser } from '../../../../graphql/graph-hooks';
import * as Constants from '../../../../helpers/constants';
import { getRoleOptions, getRolePermissionOptions } from '../../../../helpers/employee-services';
import { EMPLOYEE_ROLES } from '../../../../helpers/enums';
import * as Validations from '../../../../helpers/validations/user-validations';

export const useEmployeeForm = (businessId) => {
  const { t } = useTranslation();

  const [src, setSrc] = useState('');
  const [errors, setErrors] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [values, setValues] = useState({ permissions: [], name: '', email: '', phone_number: '', notes: '', role: '' });
  const { roles: userRoles, admin } = useUser();
  const SLUG = userRoles?.[businessId]?.slug;
  const { tier: businessTier } = useBusiness();

  useEffect(() => {
    setForm({ businessTier });
  }, [businessTier]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'permissions') {
      setValues({
        ...values,
        [name]: values.permissions.includes(value)
          ? values.permissions.filter((key) => key !== value)
          : [...values.permissions, value],
      });
    } else if (name === 'role') {
      const PERMISSIONS = getRolePermissionOptions(value, businessTier);
      setPermissions(PERMISSIONS.map((value) => ({ value, label: t(`employeePage.permissions.${value}`) })));
      setValues({ ...values, permissions: value === '' ? [] : PERMISSIONS, [name]: value });
      setErrors({ ...errors, role: Validations.validateRole(value) });
    } else {
      setValues({ ...values, [name]: value });
    }
  };

  const updateImage = useCallback(
    ({ preview, image_attachment }) => {
      setSrc(preview);
      setValues({ ...values, image_attachment });
    },
    [setSrc, values]
  );

  const validations = {
    name: Validations.validateName,
    email: Validations.validateEmail,
    notes: Validations.validateNotes,
    phone_number: Validations.validatePhone,
  };

  const handleOnBlur = useCallback(
    (e) => {
      const { name, value } = e.target;
      setValues({ ...values, [name]: value.trim() });
      setErrors({ ...errors, [name]: validations[name](value.trim()) });
    },
    [setValues, setErrors, errors, values, validations]
  );

  const isValid = useCallback(() => {
    const ERROR_OBJ = {
      name: Validations.validateName(values.name),
      email: Validations.validateEmail(values.email),
      phone_number: admin ? Validations.validatePhone(values.phone_number) : '',
      notes: admin ? Validations.validateNotes(values.notes) : '',
      role: Validations.validateRole(values.role),
    };

    setErrors(ERROR_OBJ);

    return !Object.values(ERROR_OBJ).some((msg) => msg !== '');
  }, [admin, values]);

  const setForm = useCallback(
    ({ id, name, email, phone_number, notes, first_image, roles, businessTier }) => {
      let rolePermissions = [];

      setValues({
        userId: id,
        name: name || '',
        email: email || '',
        phone_number: phone_number || '',
        notes: notes || '',
        role: roles?.[businessId]?.slug || '',
        image_attachment: null,
        permissions: roles?.[businessId]?.permissions ?? [],
      });
      setSrc(first_image?.medium ?? null);

      let allowedRoles = getRoleOptions(businessTier);

      if(!id) {
        setRoleOptions(
          allowedRoles.map(({ value }) => ({
            value,
            label: t(`employeePage.roles.${value || 'select_role'}`),
          }))
        );
        rolePermissions = getRolePermissionOptions(null, businessTier);
      } else if (admin) {
        setRoleOptions(
          allowedRoles.map(({ value }) => ({
            value,
            label: t(`employeePage.roles.${value || 'select_role'}`),
          }))
        );
        rolePermissions = getRolePermissionOptions(roles?.[businessId]?.slug || EMPLOYEE_ROLES.BOOKING_AGENT, businessTier);
      } else {
        const EMPLOYEE_ROLE = roles?.[businessId]?.slug ?? '';
        const USER_LVL = Constants.EMPLOYEE_LVLS[SLUG];
        const EMPLOYEE_LVL = Constants.EMPLOYEE_LVLS?.[EMPLOYEE_ROLE] ?? '';

        if (EMPLOYEE_LVL >= USER_LVL) {
          setRoleOptions(
            allowedRoles.map(({ value }) => ({
              value,
              label: t(`employeePage.roles.${value || 'select_role'}`),
            }))
          );
        } else {
          let INDEX = allowedRoles.findIndex(({ value }) => value === SLUG);
          if(INDEX >= 0) {
            allowedRoles = allowedRoles.slice(0, INDEX);
          }
          setRoleOptions(
            allowedRoles.map(({ value }) => ({
              value,
              label: t(`employeePage.roles.${value || 'select_role'}`),
            }))
          );
        }

        rolePermissions = getRolePermissionOptions(EMPLOYEE_ROLE, businessTier);
      }

      setPermissions(rolePermissions.map((value) => ({ value, label: t(`employeePage.permissions.${value}`) })));
    },
    [businessId, admin, SLUG]
  );

  return {
    src,
    values,
    errors,
    handleChange,
    updateImage,
    handleOnBlur,
    permissions,
    roleOptions,
    isValid,
    setForm,
  };
};
